import { useState, useEffect } from 'react';
import { Button, Stack, Text } from '@chakra-ui/react';
import { coordinateService } from '../../lib/services/coordinateService';
import { useMap } from '../map/map-context';
import { pgSelect } from '../../lib/services/postgrest';
import { fromLonLat } from 'ol/proj';
import { StorageManager } from '../../lib/utils/storage-manager';
import CustomCombobox from '../ui/combobox';

const storageManager = StorageManager.getInstance();

interface RMData {
  gid: number;
  rmno: string;
  rmnm: string;
}

export default function RMSearch({ onClose }: { onClose: () => void }) {
  const [selectedRm, setSelectedRm] = useState(() => storageManager.loadRMSelected());
  const [rmItems, setRmItems] = useState<Array<{ label: string; value: string; rmno: string }>>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRms, setLoadingRms] = useState(true);

  const { map } = useMap();

  useEffect(() => {
    loadRuralMunicipalities();
  }, []);

  // Save selected RM to storage whenever it changes
  useEffect(() => {
    storageManager.saveRMSelected(selectedRm);
  }, [selectedRm]);

  const loadRuralMunicipalities = async () => {
    try {
      const data = await pgSelect<RMData>(
        'saskadmin_2016_rural_municipality',
        'select=gid,rmno,rmnm',
      );

      if (data.resource) {
        const sortedRms = data.resource.sort((a: RMData, b: RMData) =>
          a.rmno.localeCompare(b.rmno),
        );
        const items = sortedRms.map((rm: RMData) => ({
          label: `${rm.rmno} (${rm.rmnm})`,
          value: rm.gid.toString(), // Use gid as unique identifier
          rmno: rm.rmno, // Keep rmno for coordinate lookup
        }));
        setRmItems(items);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rural municipalities');
    } finally {
      setLoadingRms(false);
    }
  };

  const handleSearch = async () => {
    if (!selectedRm) {
      setError('Please select a rural municipality');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Find the selected item to get the rmno
      const selectedItem = rmItems.find(item => item.value === selectedRm);
      if (!selectedItem) {
        setError('Selected rural municipality not found');
        return;
      }
      
      const coordinates = await coordinateService.getCoordinateByRm(selectedItem.rmno);

      if (map) {
        const view = map.getView();
        view.animate({
          center: fromLonLat([coordinates.longitude, coordinates.latitude]),
          zoom: 14,
          duration: 1000,
        });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to find RM coordinates');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap='4'>
      <Text fontSize='sm' color='gray.600'>
        Select a Rural Municipality
      </Text>

      <CustomCombobox
        items={rmItems}
        value={selectedRm}
        onValueChange={setSelectedRm}
        placeholder="Select RM"
        loading={loadingRms}
        disablePortal={true}
      />

      {error && (
        <Text color='red.500' fontSize='sm'>
          {error}
        </Text>
      )}

      <Button onClick={handleSearch} loading={loading} disabled={!selectedRm || loadingRms}>
        Search RM
      </Button>
    </Stack>
  );
}
